/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ListenerMiddlewareInstance } from "@reduxjs/toolkit";

import { Log } from "../../model/logging";
import {
  resumeOsd,
  selectIsOsdSuspended,
  selectIsOsdVisible,
  selectOsdEasCountry,
  selectOsdPriority,
  showOsd,
  suspendOsd
} from "../osd";
import {
  dismissUnityDialog,
  dismissUnityDialogWithoutAction,
  selectIsUnityDialogVisible,
  setUnityDialog
} from "../unityDialog";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";

const TAG: string = "errorDisplayListeners";

/**
 * Narrow accessor pair for the untyped `createListenerMiddleware()` instance —
 * RTK's factory is called without generics in `UnityAppStore`, so each effect
 * has to assert the concrete store types (same pattern as the existing
 * network-availability listener there).
 */
interface TypedListenerApi {
  dispatch: UnityAppDispatch;
  getState: () => UnityRootState;
}

/**
 * registerErrorDisplayListeners — cross-surface arbitration between the OSD and
 * the full-screen dialog.
 *
 * The two surfaces own their own priority guards, but nothing in either slice
 * knows the other exists — and per REDUX_GOVERNANCE.md §6.3 a reducer must not
 * read another slice's state. Kaveri does exactly that (its OSD thunk inspects
 * `getState().overlayDialog`, and `VideoPlayerUI` carries the reciprocal
 * effects in the view); here the coupling lives at the store level instead, so
 * neither slice gains a dependency on the other.
 *
 * Rules implemented:
 *  - **R4** — a dialog opening suspends the OSD (retaining its message, code and
 *    priority); the dialog closing restores it. Two surfaces are never stacked,
 *    and a transient modal does not destroy a still-relevant error underneath.
 *  - **R4-EAS / R5-EAS** — an Emergency Alert System alert is exempt from both
 *    directions. It is never suspended, and it never dismisses a dialog.
 *    Because it renders above every dialog on the z-index ladder it needs
 *    neither: the dialog stays in state, hidden behind the alert, and returns
 *    on its own when the alert ends. Without the first half an ordinary modal
 *    would take an emergency off the screen; without the second, the emergency
 *    would destroy a message the viewer had not dealt with.
 *  - **R5** — an OSD becoming visible dismisses an open dialog. OSD codes
 *    outrank dialog codes on the ladder (995–999 vs 994), so the higher-priority
 *    condition takes the screen.
 *
 * Each effect re-reads state and returns early when there is nothing to do, so
 * R4 and R5 cannot drive each other in a loop: `suspendOsd` leaves the OSD
 * invisible (so R5's guard fails), and `resumeOsd` only fires from a dismiss
 * (so the dialog is already gone when R5 next evaluates).
 *
 * @param listenerMiddleware - The store's listener middleware instance.
 */
export function registerErrorDisplayListeners(
  listenerMiddleware: ListenerMiddlewareInstance
): void {
  // R4 (open) — a dialog took the screen: park the OSD, keep its content.
  listenerMiddleware.startListening({
    actionCreator: setUnityDialog,
    effect: (_action, listenerApi): void => {
      const { dispatch, getState } = listenerApi as unknown as TypedListenerApi;
      const state: UnityRootState = getState();
      // `setUnityDialog` may have been rejected by its own priority guard, so
      // confirm a dialog is actually up before hiding anything.
      if (selectIsUnityDialogVisible(state) === false) {
        return;
      }
      if (selectIsOsdVisible(state) === false) {
        return;
      }
      // R4-EAS. The alert is neither suspended nor allowed to be covered: it
      // renders above every dialog on the z-index ladder, so the dialog is
      // simply left where it is, hidden behind the alert and intact. It comes
      // back by itself once the alert goes.
      //
      // Dismissing it instead would satisfy "nothing covers the alert" just as
      // well, but at the cost of destroying a message the viewer has not dealt
      // with — an emergency interrupts the screen, it does not clear the
      // viewer's business off it.
      if (selectOsdEasCountry(state).length > 0) {
        Log.i(
          TAG,
          "dialog shown during EAS alert — leaving both in place, alert renders above it"
        );
        return;
      }
      Log.i(TAG, "dialog shown — suspending OSD");
      dispatch(suspendOsd());
    }
  });

  // R4 (close) — the dialog is gone: bring back whatever it displaced.
  listenerMiddleware.startListening({
    actionCreator: dismissUnityDialog,
    effect: (_action, listenerApi): void => {
      const { dispatch, getState } = listenerApi as unknown as TypedListenerApi;
      const state: UnityRootState = getState();
      if (selectIsOsdSuspended(state) === false) {
        return;
      }
      if (selectOsdPriority(state) === 0) {
        return;
      }
      Log.i(TAG, "dialog dismissed — restoring suspended OSD");
      dispatch(resumeOsd());
    }
  });

  // R5 — a higher-priority OSD condition appeared: close the dialog.
  listenerMiddleware.startListening({
    actionCreator: showOsd,
    effect: (_action, listenerApi): void => {
      const { dispatch, getState } = listenerApi as unknown as TypedListenerApi;
      const state: UnityRootState = getState();
      // `showOsd` may have lost its own priority guard.
      if (selectIsOsdVisible(state) === false) {
        return;
      }
      if (selectIsUnityDialogVisible(state) === false) {
        return;
      }
      // R5-EAS. An EAS alert is the one OSD that must not clear the dialog:
      // it outranks it visually without needing to, and the viewer is owed
      // the dialog back once the emergency has passed.
      if (selectOsdEasCountry(state).length > 0) {
        Log.i(TAG, "EAS alert shown — dialog kept, it will be covered");
        return;
      }
      Log.i(TAG, "OSD shown — dismissing dialog");
      dismissUnityDialogWithoutAction(dispatch);
    }
  });
}
